import { Component, ElementRef, AfterContentInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { filter } from 'rxjs';

import { DistributionService } from '../../../../../Common/TargetingShared/services/distribution.service';
import { DistributionModel } from '../../../models/distribution.model';
import { DiscoverRoutingService } from '../../../../DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteModel } from '../../../../DiscoverRouter/models/discover-route.model';
import { StepsNavigationComponent } from '../../../../../Common/Layout/components/StepsNavigation/steps-navigation.component';
import { StepsBannerErrorConfigModel } from '../../../../../Common/Misc/models/banner-error-config.model';
import { StepStatesEnum } from '@Common/Layout/components/StepsNavigation/step-states.enum';

export enum DistributionStepsEnum {
    compose,
    editRecipients,
    personalize,
    review
}

export enum DistributionStepIds {
    compose = 'compose_distribution',
    editRecipients = 'edit_recipients_distribution',
    personalize = 'personalize',
    review = 'review_distribution',
}

@Component({
    selector: 'agpr-distribution-steps-navigation',
    templateUrl: '../../../../../Common/Layout/components/StepsNavigation/steps-navigation.component.html',
    styleUrls: ['../../../../../Common/Layout/components/StepsNavigation/steps-navigation.component.scss'],
    standalone: false
})
export class DistributionStepsNavigationComponent extends StepsNavigationComponent implements AfterContentInit, OnChanges, OnDestroy {

    distribution: DistributionModel;
    currentRoute: DiscoverRouteModel;

    readonly tabIdToIndex = new Map<string, number>([
        [DistributionStepIds.compose, DistributionStepsEnum.compose],
        [DistributionStepIds.editRecipients, DistributionStepsEnum.editRecipients],
        [DistributionStepIds.personalize, DistributionStepsEnum.personalize],
        [DistributionStepIds.review, DistributionStepsEnum.review],
    ]);

    private tabIndexToString: Map<DistributionStepsEnum, string> = new Map([
        [DistributionStepsEnum.compose, 'compose'],
        [DistributionStepsEnum.editRecipients, 'editRecipients'],
        [DistributionStepsEnum.personalize, 'personalize'],
        [DistributionStepsEnum.review, 'review'],
    ]);

    queryParams: any = {};

    constructor(
        protected el: ElementRef<HTMLElement>,
        public distributionService: DistributionService,
        public discoverRoutingService: DiscoverRoutingService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
    ) {
        super(el);
    }

    ngAfterContentInit() {
        super.ngAfterContentInit();
        requestAnimationFrame(() => {
            this.subscriptions.push(
                this.distributionService.getFocusedResource().pipe(filter(x => !!x)).subscribe(distribution => {
                    const distribChanged = !this.distribution || distribution.id !== this.distribution.id;
                    this.distribution = distribution;
                    if (distribChanged) {
                        this.allErrors = [];
                        this.clearStepStates(DistributionStepIds.compose);
                        this.setClasses();
                    }
                }),
                this.discoverRoutingService.getCurrentRoute().subscribe(
                    route => {
                        this.currentRoute = route;
                        if (this.currentRoute.queryParamMap?.get('step')) {
                            this.selectTabByIndex(
                                Array.from(this.tabIndexToString.values()).findIndex(x => x === this.currentRoute.queryParamMap.get('step')),
                                true,
                                false
                            );
                        } else {
                            this.selectTabByIndex(DistributionStepsEnum.compose, true, false);
                        }
                    }
                )
            );
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('selectedTabIndex')) {
            if (this.distribution) {
                const stepValue = this.tabIndexToString.get(changes['selectedTabIndex'].currentValue);
                this.router.navigate(
                    [],
                    {
                        relativeTo: this.activatedRoute,
                        queryParams: { step: stepValue },
                        queryParamsHandling: 'merge'
                    }
                );
            }
            this.handleStepStates(this.allErrors);
            this.setClasses();
        }
        if (changes.hasOwnProperty('allErrors')) {
            this.handleStepStates(this.allErrors);
            this.setClasses();
        }
    }

    /**
     * Update the distribution step states
     */
    protected handleStepStates(errors: StepsBannerErrorConfigModel[]) {
        if (!this.tabs || this.tabs.length === 0) {
            return;
        }

        // touched state is defined by a tab that is behind or equal to the current selected tab
        // all touch states must be calculated before tab state calculations
        this.tabs?.toArray().forEach(tab => {
            if (this.tabIdToIndex.get(tab.id) <= this._selectedTabIndex) {
                tab.touched = true;
            }
        });

        this.tabs?.toArray().forEach(tab => {
            let hasErrors: boolean = false;
            switch(tab.id) {
                case DistributionStepIds.compose:
                    hasErrors = !!errors.find(x => x.tab === DistributionStepsEnum.compose);
                    break;
                case DistributionStepIds.editRecipients:
                    hasErrors = !!errors.find(x => x.tab === DistributionStepsEnum.editRecipients);
                    break;
                case DistributionStepIds.personalize:
                    hasErrors = !!errors.find(x => x.tab === DistributionStepsEnum.personalize);
                    break;
                default:
            }

            if (tab.selected) {
                tab.iconState = StepStatesEnum.selected;
            } else if (tab.touched === false) {
                tab.iconState = StepStatesEnum.open;
            } else if (tab.touched === true) {
                if (hasErrors) {
                    tab.error = true;
                    const reviewTab = this.tabs.toArray().find(x => x.id === DistributionStepIds.review);
                    if (reviewTab?.touched) {
                        // only show error state on tabs when review tab has been touched
                        tab.iconState = StepStatesEnum.error;
                    } else {
                        tab.iconState = StepStatesEnum.inProgress;
                    }
                } else {
                    // special case, since review step doesn't have an error state, calculate state based on if other steps have errors
                    if (tab.id === DistributionStepIds.review) {
                        tab.iconState = (this.tabs.toArray().some(x => x.error)) ? StepStatesEnum.inProgress
                            : StepStatesEnum.completed;
                    } else {
                        tab.error = false;
                        tab.iconState = StepStatesEnum.completed;
                    }
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
